/**
 * 招待受諾ページ コンテナ
 */

import { AuthContext } from "hooks/authProvider/authProvider";
import { useAcceptInvitation, useInvitation } from "hooks/useInvitationApi";
import type React from "react";
import { useCallback, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { InvitationPage } from "routes/InvitationPage/InvitationPage";
import { toastError } from "utils/toast-utils";

const InvitationPageContainer: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { data, loading, error, refetch } = useInvitation(token || "");
  const { currentUser, isLoadingUser, signOut, refetchMe } =
    useContext(AuthContext);
  const { acceptInvitation, loading: accepting } = useAcceptInvitation();
  const [signingOut, setSigningOut] = useState(false);
  const navigate = useNavigate();

  const handleAccept = useCallback(async () => {
    if (!token) return;
    try {
      const tenant = await acceptInvitation(token);
      // /auth/me を取り直してテナント情報を反映させてからダッシュボードへ
      await refetchMe?.();
      toast.success(`${tenant.name} に参加しました`);
      navigate("/");
    } catch (e) {
      toastError(e instanceof Error ? e.message : "招待の承認に失敗しました");
      // 既に参加済み / 期限切れ等で状態が変わっている可能性があるため再取得
      await refetch();
    }
  }, [token, acceptInvitation, refetchMe, navigate, refetch]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/login");
    } finally {
      setSigningOut(false);
    }
  }, [signOut, navigate]);

  return (
    <InvitationPage
      invitation={data}
      loading={loading || isLoadingUser}
      error={error ? { status: error.status, message: error.message } : null}
      token={token || ""}
      currentUserEmail={currentUser?.email ?? null}
      onAccept={handleAccept}
      accepting={accepting || signingOut}
      onSignOut={handleSignOut}
    />
  );
};

export default InvitationPageContainer;
